/**
 * The record on a line (`inoutTimeline`) — the window of the rule that binds
 * first, the trips as bars (one still open runs to today, a planned one is
 * outlined), the days nobody confirmed shaded, the renewal window as a band,
 * and today. What no list explains
 * at a glance: where the time away sits in the window, and what is ahead.
 * For assistive technology the same trips are a list. A window with nothing
 * to draw is one line that says so, with the page's way to add a trip.
 */
import { Route } from '@tonnie/assets';
import { inoutClockNameKey, inoutHeadlineView, inoutTimeline } from '@tonnie/heroes';
import { useCountryName, useLocale } from '@tonnie/locale';
import type { InOutReading } from '@tonnie/sdk';
import { Card, CardContent, CardHeader, CardTitle, cn } from '@tonnie/ui';

import { INOUT_CLASSES } from '../lib/inout-tone';

interface InOutTimelineProps {
  status: InOutReading;
  absences: ReadonlyArray<{ id: string; departedOn: string; returnedOn: string | null }>;
  plannedTrips?: ReadonlyArray<{ id: string; departsOn: string; returnsOn: string; state: string }>;
}

/** Days nobody confirmed: hatched, never filled like a trip. */
const UNCERTAIN_LOOK =
  'bg-[repeating-linear-gradient(135deg,var(--color-muted-foreground)_0_2px,transparent_2px_6px)] opacity-30';

const BAR_LOOK = {
  trip: INOUT_CLASSES.solid,
  away: cn('ring-2 ring-offset-1 ring-offset-card', INOUT_CLASSES.solid, INOUT_CLASSES.ringLine),
  planned: cn('border-2 border-dashed', INOUT_CLASSES.edge, INOUT_CLASSES.subtle),
} as const;

/** What each bar is, for a screen reader and the legend. */
function barName(kind: 'trip' | 'away' | 'planned', t: (key: string) => string): string {
  switch (kind) {
    case 'trip':
      return t('inout.timeline.trip');
    case 'away':
      return t('inout.timeline.away');
    case 'planned':
      return t('inout.timeline.planned');
    default: {
      const unreachable: never = kind;
      throw new Error(`Unknown bar: ${String(unreachable)}`);
    }
  }
}

/** Near an end of the line, "Today" leans inward — never past the card's edge. */
const edgeOf = (at: number): 'start' | 'middle' | 'end' =>
  at < 12 ? 'start' : at > 88 ? 'end' : 'middle';
const TODAY_ALIGN = {
  start: 'items-start',
  middle: '-translate-x-1/2 items-center',
  end: '-translate-x-full items-end',
} as const;

export function InOutTimeline({ status, absences, plannedTrips = [] }: InOutTimelineProps) {
  const { t, formatDate } = useLocale();
  const countryName = useCountryName();
  const line = inoutTimeline({ evaluation: status.evaluation, absences, plannedTrips });
  if (!line) return null;
  const headline = inoutHeadlineView(status.evaluation).clock;
  const date = (iso: string) => formatDate(iso, 'calendarDay');
  const range = (from: string, to: string | null) =>
    to
      ? t('inout.absence.range', { from: date(from), to: date(to) })
      : t('inout.absence.stillAway', { from: date(from) });

  // Nothing to draw in the window — no trip, no day to confirm: one line, not
  // an empty rail (owner, 19 Oct 2026). It stands beside the trips' own form.
  if (line.bars.length === 0 && line.uncertain.length === 0) {
    return (
      <Card data-testid="inout-timeline">
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Route className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p
            className="min-w-0 flex-1 text-sm text-muted-foreground"
            data-testid="inout-timeline-empty"
          >
            {t('inout.timeline.emptyWindow', { from: date(line.from), to: date(line.to) })}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="inout-timeline">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('inout.timeline.title')}</CardTitle>
        {headline && (
          <p className="text-sm text-muted-foreground">
            {t('inout.timeline.window', {
              rule: t(inoutClockNameKey(headline), {
                country: countryName(status.evaluation.country),
              }),
            })}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative pt-6" aria-hidden="true">
          <div className="relative h-10 overflow-hidden rounded-lg bg-muted">
            {line.renewal && (
              <div
                className="absolute inset-y-0 border-x border-dashed border-tone-amber-line bg-tone-amber-subtle"
                style={{
                  left: `${line.renewal.start}%`,
                  width: `${line.renewal.end - line.renewal.start}%`,
                }}
                data-testid="inout-timeline-renewal"
              />
            )}
            {line.uncertain.map((u) => (
              <div
                key={`uncertain-${u.from}`}
                className={cn('absolute inset-y-0', UNCERTAIN_LOOK)}
                style={{ left: `${u.start}%`, width: `${Math.max(u.end - u.start, 0.6)}%` }}
                data-testid="inout-timeline-uncertain"
              />
            ))}
            {line.bars.map((bar) => (
              <div
                key={bar.key}
                className={cn(
                  'absolute top-2 h-6 min-w-1.5 rounded-[calc(var(--radius)/3)]',
                  BAR_LOOK[bar.kind]
                )}
                style={{ left: `${bar.start}%`, width: `${Math.max(bar.end - bar.start, 0.6)}%` }}
                title={range(bar.from, bar.to)}
                data-testid="inout-timeline-bar"
                data-kind={bar.kind}
              />
            ))}
          </div>
          {line.today !== null && (
            <div
              className={cn(
                'absolute top-0 bottom-0 flex flex-col',
                TODAY_ALIGN[edgeOf(line.today)]
              )}
              style={{ left: `${line.today}%` }}
              data-testid="inout-timeline-today"
            >
              <span className="rounded-[calc(var(--radius)/3)] bg-foreground px-1.5 text-2xs font-semibold leading-5 text-background">
                {t('inout.timeline.today')}
              </span>
              <span className="w-0.5 flex-1 bg-foreground" />
            </div>
          )}
        </div>
        <div className="flex justify-between text-xs tabular-nums text-muted-foreground">
          <span>{date(line.from)}</span>
          <span>{date(line.to)}</span>
        </div>
        <ul
          className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
          aria-hidden="true"
        >
          {line.bars.some((bar) => bar.kind === 'trip') && (
            <li className="flex items-center gap-1.5" data-testid="inout-timeline-key-trip">
              <span className={cn('h-2.5 w-4 rounded-sm', INOUT_CLASSES.solid)} />
              {t('inout.timeline.trip')}
            </li>
          )}
          {line.bars.some((bar) => bar.kind === 'away') && (
            <li className="flex items-center gap-1.5" data-testid="inout-timeline-key-away">
              <span className={cn('h-2.5 w-4 rounded-sm', BAR_LOOK.away)} />
              {t('inout.timeline.away')}
            </li>
          )}
          {line.bars.some((bar) => bar.kind === 'planned') && (
            <li className="flex items-center gap-1.5" data-testid="inout-timeline-key-planned">
              <span
                className={cn('h-2.5 w-4 rounded-sm border-2 border-dashed', INOUT_CLASSES.edge)}
              />
              {t('inout.timeline.planned')}
            </li>
          )}
          {line.uncertain.length > 0 && (
            <li className="flex items-center gap-1.5" data-testid="inout-timeline-key-uncertain">
              <span className={cn('h-2.5 w-4 rounded-sm', UNCERTAIN_LOOK)} />
              {t('inout.timeline.uncertain')}
            </li>
          )}
          {line.renewal && (
            <li className="flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm bg-tone-amber-subtle ring-1 ring-tone-amber-line" />
              {t('inout.timeline.renewal')}
            </li>
          )}
        </ul>
        <ul className="sr-only">
          {line.bars.map((bar) => (
            <li key={bar.key}>
              {barName(bar.kind, t)}: {range(bar.from, bar.to)}
            </li>
          ))}
          {line.uncertain.map((u) => (
            <li key={`uncertain-${u.from}`}>
              {t('inout.timeline.uncertain')}: {range(u.from, u.to)}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
